import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Select, Typography } from 'antd';
import { DownloadOutlined, FileImageOutlined, FilePdfOutlined, LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons/faExternalLinkAlt";
import { faFilterCircleXmark } from "@fortawesome/free-solid-svg-icons/faFilterCircleXmark";
import dayjs from "dayjs";
import ConsignmentDeliveryDate from "../../../components/ConsignmentDeliveryDate";
import AddressSuburbPopover from "../AddressSuburbPopover";
import RequireTenantSelection from "../../../components/RequireTenantSelection";
import TenantPartnershipResource from "../../tenants/api/TenantPartnershipResource";
import { axiosErrorHandler } from "../../../../../swzutils";
import { CustomerAPIConsignmentStatusEnum } from "../api/Representation";
import ConsignmentFreightPopover from "../ConsignmentFreightPopover";
import PermissionDropdownButton from "../../../components/PermissionDropdownButton";
import GlobalConstants from "../../../../../swzutils/GlobalConstants";
import PartnerConsignmentStatusEnumify from "./PartnerConsignmentStatusEnumify";
import ImageGalleryLightbox from "../../../components/ImageGalleryLightbox";
import ConsignmentImageKind from "../../../enums/ConsignmentImageKind";
import PaginatedFilterableTable, {} from "../../../components/table/PaginatedFilterableTable";
import PermissionButton from "../../../components/PermissionButton";
import { useAppSelector } from "../../../../../store";
import { selectedTenantSelector } from "../../../../../store/slice/auth";
const PartnerConsignmentsTable = () => {
    const [partnerCustomers, setPartnerCustomers] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(undefined);
    const [showImagesConsignment, setShowImagesConsignment] = useState(undefined);
    const tableRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const selectedTenant = useAppSelector(selectedTenantSelector);
    // Track the loaded tenant separate from the selected tenant to prevent an error showing when multi tenant users switch tenants
    const [loadedTenantPartner, setLoadedTenantPartner] = useState(undefined);
    useEffect(() => {
        setPartnerCustomers(null);
        setSelectedCustomer(undefined);
        if (selectedTenant?.id) {
            setLoadedTenantPartner(selectedTenant);
            loadPartners(selectedTenant.id);
        }
    }, [selectedTenant?.id]);
    const refresh = () => {
        tableRef.current?.fetch();
    };
    const clearAllFilters = () => {
        tableRef.current?.clearAllFilters();
    };
    const downloadCsv = () => {
        tableRef.current?.downloadCurrentFilterAsCsv("consignments");
    };
    const onPartnersLoaded = (partners) => {
        if (partners.length > 0) {
            setPartnerCustomers(partners);
            setSelectedCustomer(partners[0]);
        }
        else {
            setPartnerCustomers(null);
        }
    };
    const loadPartners = (relationTenantId) => {
        setLoading(true);
        TenantPartnershipResource.getTenantCustomerOfPartnerships(relationTenantId, onPartnersLoaded, axiosErrorHandler, () => setLoading(false));
    };
    const onPartnerSelected = (customerId) => {
        if (partnerCustomers && partnerCustomers.length) {
            setSelectedCustomer(partnerCustomers.find(p => p.id == customerId));
        }
    };
    const loadConsignmentImages = (consignment) => {
        setShowImagesConsignment(consignment);
    };
    const columns = [{
            key: 'effectiveDate',
            title: 'Date / Time',
            dataIndex: 'effectiveDate',
            width: '10%',
            filter: {
                type: 'dateRange',
                requestKey: 'deliveryOnOrAfter',
                requestKeyDateTo: 'deliveryOnOrBefore'
            },
            render: (text, consignment) => (_jsx(ConsignmentDeliveryDate, { consignment: consignment }))
        }, {
            title: 'Delivery Reference',
            dataIndex: 'consignmentRef',
            width: '5%',
            filter: {
                type: 'text',
                requestKey: 'consignmentRefLike',
                placeholder: 'Search ref',
            }
        }, {
            title: 'Your Reference',
            dataIndex: 'customerRef',
            width: '5%',
            filter: {
                type: 'text',
                requestKey: 'customerRefLike',
                placeholder: 'Search ref',
            }
        }, {
            title: 'Vehicle Type',
            dataIndex: ['deliveryVehicle', 'type', 'description'],
            width: '10%'
        }, {
            title: 'Rego',
            dataIndex: ['deliveryVehicle', 'registration'],
            width: '5%'
        }, {
            title: 'Delivery Address',
            dataIndex: 'customerRef',
            width: '20%',
            render: (text, consignment) => (_jsx(AddressSuburbPopover, { title: consignment.deliveryCompanyName, address: consignment.deliveryAddress, isPickup: false })),
        }, {
            title: 'Status',
            dataIndex: 'status',
            width: '10%',
            render: (text, consignment) => {
                if (consignment.status == CustomerAPIConsignmentStatusEnum.COMPLETED) {
                    const completedAt = dayjs(consignment.completedAt);
                    return (_jsxs(_Fragment, { children: [PartnerConsignmentStatusEnumify.renderTag(consignment.status), _jsx("br", {}), completedAt.format("DD/MM/YYYY HH:mm")] }));
                }
                if (consignment.status == CustomerAPIConsignmentStatusEnum.ARRIVED && consignment.deliveryArrivedAt) {
                    const deliveryArrivedAt = dayjs(consignment.deliveryArrivedAt);
                    return (_jsxs(_Fragment, { children: [PartnerConsignmentStatusEnumify.renderTag(consignment.status), _jsx("br", {}), deliveryArrivedAt.format("DD/MM/YYYY HH:mm")] }));
                }
                return PartnerConsignmentStatusEnumify.renderTag(consignment.status);
            }
        }, {
            title: 'Freight',
            dataIndex: 'customerRef',
            width: '20%',
            render: (text, consignment) => (_jsx(ConsignmentFreightPopover, { consignment: consignment, forcePopover: false }))
        }, {
            title: 'Action',
            width: '10%',
            render: (text, consignment) => {
                return (_jsx(PermissionDropdownButton, { label: _jsxs(_Fragment, { children: [_jsx(FilePdfOutlined, {}), "\u00A0POD"] }), 
                    // @ts-ignore
                    onClick: () => window.open(`${apiOrigin()}/admin/v1/tenant-partners/${selectedTenant?.id}/customers/${selectedCustomer?.id}/consignments/${consignment.id}/proof-of-delivery`, GlobalConstants.PDF_WINDOW_TARGET_NAME), permissions: ['admin:consignment:view'], menuItems: [{
                            key: "trackingPortalLink",
                            condition: !!consignment.trackingPortalLink,
                            onClick: () => {
                                window.open(consignment.trackingPortalLink);
                            },
                            icon: _jsx(FontAwesomeIcon, { icon: faExternalLinkAlt }),
                            label: "Live Tracking"
                        }, {
                            key: "viewImages",
                            condition: consignment.images?.length > 0,
                            onClick: () => loadConsignmentImages(consignment),
                            icon: _jsx(FileImageOutlined, {}),
                            label: "Delivery Photos"
                        }] }));
            }
        }];
    return (_jsxs(RequireTenantSelection, { children: [loading && _jsxs(Typography.Title, { className: "text-center", children: [_jsx(LoadingOutlined, { spin: true }), " Loading..."] }), !loading && !selectedCustomer &&
                _jsx(Alert, { title: "No Partners Found", description: "Please contact SubTrux support using the link at the bottomn left if you would to have your partners added here", type: "info", showIcon: true }), selectedTenant && selectedCustomer && loadedTenantPartner && loadedTenantPartner.id === selectedTenant.id &&
                _jsxs(_Fragment, { children: [_jsxs("div", { className: "table-operations", children: [_jsx(Button, { className: "pull-right", onClick: refresh, title: "Refresh", icon: _jsx(ReloadOutlined, {}) }), _jsx(Button, { className: "pull-right", onClick: clearAllFilters, title: "Clear All Filter", icon: _jsx(FontAwesomeIcon, { icon: faFilterCircleXmark }) }), _jsx(PermissionButton, { permissions: ['admin:consignmentPartnershipExport:csv'], className: "pull-right", onClick: downloadCsv, title: "Download CSV", icon: _jsx(DownloadOutlined, {}), children: "CSV" }), partnerCustomers && partnerCustomers.length > 1 &&
                                    _jsx(Select, { value: selectedCustomer.id, className: "pull-right", style: { width: 250 }, onChange: onPartnerSelected, options: partnerCustomers.map(p => {
                                            return {
                                                value: p.id,
                                                label: `${p.tenant.name} - ${p.name}`
                                            };
                                        }) }), _jsx("h2", { children: selectedCustomer.tenant.name })] }), _jsx(PaginatedFilterableTable, { tableKey: 'PartnerConsignmentsTable', ref: tableRef, dataUri: `/admin/v1/tenant-partners/${selectedTenant.id}/customers/${selectedCustomer.id}/consignments`, columns: columns }), _jsx(ImageGalleryLightbox, { open: !!showImagesConsignment, initialIndex: 0, images: (showImagesConsignment?.images || []).map(it => {
                                return {
                                    // @ts-ignore
                                    url: `${apiOrigin()}/admin/v1/tenant-partners/${selectedTenant.id}/customers/${selectedCustomer.id}/consignments/${showImagesConsignment?.id}/images/${it.id}`,
                                    title: ConsignmentImageKind.enumDescription(it.kind),
                                    caption: it.comments
                                };
                            }), onClose: () => {
                                setShowImagesConsignment(undefined);
                            } })] })] }));
};
export default PartnerConsignmentsTable;
